import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { sortDates, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../../constants/common';
import { BLANK_FILTER_TEXT } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { fetchFarms } from '../../../redux/actions/farm';
import { fetchTags } from '../../../redux/actions/tag';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setSortedData } from '../../../redux/reducers/tag';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import StatusIndicator from '../../../components/StatusIndicator';
import { errorToastHandler, successToastHandler } from '../../../components/action_notifier';

import { SocketContext } from '../../../context/socket';
import { TagService } from '../../../services';
import { useFilterOptions } from './useFilterOptions';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function TagsTable() {
  const { socket } = useContext(SocketContext);

  const dispatch = useDispatch();

  const { selectedFilters } = useSelector(state => state.filters);
  const { options: farmOptions } = useSelector(state => state.farm);
  const { tags, loading: isTagsLoading, sortedData } = useSelector(state => state.tag);
  const { permissions } = useSelector(state => state.user);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [state, setState] = useState({
    delete_modal: false,
    editableIds: [],
    query: '',
    search_livestock: '',
    selected_tags: [],
    tags: [],
    ws: null
  });

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    if (socket && !state.ws) {
      setState(prevState => ({ ...prevState, ws: socket }));

      socket.on('tagStatus', data => {
        setState(prevState => ({
          ...prevState,
          tags: state.tags?.map(item => {
            let status = item.status;

            if (data.ids.includes(item.id)) {
              status = data.status;
            }

            return { ...item, status };
          })
        }));
      });
    }

    return () => socket?.off('tagStatus');
  }, [socket]);

  useEffect(() => {
    setTagsData();

    if (tags?.length) {
      dispatch(setSortedData(tags));
    }
  }, [tags]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && tags?.length) {
      dispatch(setSortedData(tags));
    }
  }, [filteredInfo]);

  async function fetchData() {
    try {
      await Promise.all([getTags(), getFarms()]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const setFilters = (pagination, filters) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilters(pagination, filters);
      dispatch(setSortedData(currentDataSource));
    },
    [setSortedData, dispatch]
  );

  function setTagsData() {
    if (!tags) return;

    setState(prevState => ({ ...prevState, tags }));
  }

  async function getFarms() {
    await dispatch(fetchFarms({ query: state.query || null })).unwrap();
  }

  async function getTags(query) {
    await dispatch(fetchTags({ query }));
  }

  const toggleModal = modal => {
    setState(state => ({ ...state, [modal]: !state[modal] }));
  };

  const setDeleteTags = () => {
    setState(prevState => ({ ...prevState, delete_tags: state.selected_tags }));
    toggleModal('delete_modal');
  };

  const deleteTag = async () => {
    const response = await axios.delete('tags/bulk', {
      data: {
        ids: state.delete_tags
      }
    });

    if (response.status === 200) {
      await getTags();
      toggleModal('delete_modal');
      setState(prevState => ({ ...prevState, selected_tags: [] }));
    }
  };

  const onClickSaveEdit = async data => {
    const payload = { farm_id: data?.at(0).farm?.value };

    try {
      await TagService.updateOne(data?.at(0).id, payload);
      setState(prevState => ({ ...prevState, editableIds: [], selected_tags: [] }));
      await getTags();
      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  };

  const selectRecords = selected => {
    setState(prevState => ({
      ...prevState,
      selected_tags: selected
    }));
  };

  const actions = useMemo(() => {
    const { selected_tags } = state;

    const availableActions = [];

    if (hasPermission([PERMISSIONS.TAGS.DELETE_OWN], permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" disabled={!selected_tags?.length} onClick={setDeleteTags}>
            Archive Kraal Tag
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [selectedFilters, state.selected_tags]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Kraal Tag ID',
      dataIndex: 'diagri_id',
      configColumnKey: TABLE_CONFIG_COLUMNS.KRAAL_TAG_ID,
      filteredValue: filteredInfo.diagri_id || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: (value, record) => <Link to={`/tag/${record.id}`}>{record.diagri_id || '/'}</Link>,
      sorter: (a, b) => sortStrings(a.diagri_id, b.diagri_id)
    },
    {
      ...commonColumnProperties,
      title: 'Livestock ID',
      dataIndex: 'livestockId',
      configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
      filteredValue: filteredInfo.livestockId || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => {
        const searchField = record?.animal?.identifier || 'Unlinked';

        return searchField.toString().toLowerCase().includes(value.toLowerCase());
      },
      render: (value, record) =>
        record?.animal ? <Link to={`/animals/${record?.animal?.id}`}>{record?.animal?.identifier}</Link> : 'Unlinked',
      sorter: (a, b) => sortStrings(a.animal?.identifier, b.animal?.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Farm',
      dataIndex: 'farm',
      configColumnKey: TABLE_CONFIG_COLUMNS.FARM,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: farmOptions,
        valueKey: 'id'
      },
      filterSearch: true,
      filteredValue: filteredInfo.farm || null,
      filters: columnFilterOptions.farms,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.farm?.name : record?.farm?.name === value),
      sorter: (a, b) => sortStrings(a.farm?.name, b.farm?.name),
      render: (value, record) => value?.name ?? ''
    },
    {
      ...commonColumnProperties,
      title: 'Location',
      dataIndex: 'animal',
      configColumnKey: TABLE_CONFIG_COLUMNS.LOCATION,
      filteredValue: filteredInfo.animal || null,
      filters: columnFilterOptions.locations,
      width: COLUMN_SIZE.XXL,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT
          ? !record?.animal?.geofences?.length
          : record?.animal?.geofences?.at(0)?.name === value,
      render: (value, record) => (
        <Link to={`tracking/${record?.animal?.id}`}>{record?.animal?.geofences?.at(0)?.name}</Link>
      ),
      sorter: (a, b) => sortStrings(a.animal?.geofences?.at(0)?.name, b.animal?.geofences?.at(0)?.name)
    },
    {
      ...commonColumnProperties,
      title: 'Battery Status',
      dataIndex: 'battery_status',
      configColumnKey: TABLE_CONFIG_COLUMNS.BATTERY_STATUS,
      filteredValue: filteredInfo.battery_status || null,
      filters: columnFilterOptions.batteryStatuses,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => record?.battery_status_label.toLowerCase() === value.toLowerCase(),
      sorter: (a, b) => sortStrings(a?.battery_status_label, b?.battery_status_label),
      render: (value, record) => (
        <div className="d-flex align-items-center text-capitalize">
          <StatusIndicator key={record?.id} status={record?.battery_status} />
          {record.battery_status_label}
        </div>
      )
    },
    {
      ...commonColumnProperties,
      title: 'Connection Status',
      dataIndex: 'status',
      configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTION_STATUS,
      filteredValue: filteredInfo.status || null,
      filters: columnFilterOptions.connectionStatuses,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => record?.status === value,
      sorter: (a, b) => sortStrings(a?.status, b?.status),
      render: (value, record) => {
        return (
          record && (
            <div className="d-flex align-items-center text-capitalize">
              <StatusIndicator key={record?.id} status={record?.status} />
              {record?.status}
            </div>
          )
        );
      }
    },
    {
      ...commonColumnProperties,
      title: 'Last Reading',
      dataIndex: 'timestamp_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.LAST_READING,
      filteredValue: filteredInfo.timestamp_at || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => record?.timestamp_at?.includes(value.toLowerCase()),
      sorter: (a, b) => sortDates(a?.timestamp_at, b?.timestamp_at),
      render: (value, record) => {
        const lastReadingTime = record?.timestamp_at;

        return lastReadingTime ? dayjs(lastReadingTime).format(DATE_FORMAT.DATETIME) : null;
      }
    },
    {
      ...commonColumnProperties,
      title: 'Linked Status',
      dataIndex: 'animal_status',
      configColumnKey: TABLE_CONFIG_COLUMNS.LINKED_STATUS,
      filteredValue: filteredInfo.animal_status || null,
      filters: columnFilterOptions.linkedStatuses,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => (value === 'Linked' ? record?.animal : !record?.animal),
      sorter: (a, b) => {
        const strA = a.animal ? 'Linked' : 'Not Linked';
        const strB = b.animal ? 'Linked' : 'Not Linked';

        return sortStrings(strA, strB);
      },
      render: (value, record) => (record.animal ? 'Linked' : 'Not Linked')
    }
  ];

  return (
    <>
      <AppTable
        headerClass="py-2"
        actions={actions}
        baseColumns={columns}
        dataSource={state.tags}
        tableConfigKey={TABLE_CONFIG_KEYS.TAGS}
        filterable={false}
        loading={isTagsLoading}
        searchDefaultValue={state.query}
        searchPlaceholder="Search Kraal tags"
        handleOnChange={handleTableChange}
        handleOnSelectRecords={selectRecords}
        handleSearchChange={getTags}
        updateTableData={onClickSaveEdit}
      />

      <AppModal
        isOpen={state.delete_modal}
        confirmButtonColor="danger"
        confirmButtonText="Archive"
        title="Archive tags"
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={deleteTag}>
        <div className="py-4">{messages.ARCHIVE_CONFIRMATION('tags')}</div>
      </AppModal>
    </>
  );
}

export default memo(TagsTable);
